import React from "react";
import { Circle, CircularProgress, CircularTimerWrapper } from "../styles";
import TimerDisplay from "./timerDisplay";
import { TimerInfoState } from "../../../timer-controller";
import { TournamentResponse } from "../../../../api/tournament";
import { calculatePercentage } from "../../../../utils/timer";
import { StructuresResponse } from "../../../tournament/hook/useEditTournament";

interface CircularTimerProps {
  radius: number;
  timeInfo: TimerInfoState;
}

const calculateCircumference = (radius: number): number => 2 * Math.PI * radius;

const calculateStrokeDashoffset = (
  circumference: number,
  remainingMs: number | null,
  totalTimeMs: number | null
): number => {
  if (!remainingMs || !totalTimeMs) return 0;
  return circumference - (remainingMs / totalTimeMs) * circumference;
};

const CircularTimer: React.FC<CircularTimerProps> = ({ radius, timeInfo }) => {
  const circumference = calculateCircumference(radius);
  const isBreak =
    timeInfo.currentLevel?.type === "BREAK" ||
    (timeInfo.elapsedTimeMs && timeInfo.elapsedTimeMs < 0)
      ? true
      : false;

  return (
    <Circle break={isBreak}>
      <circle className="background" r={radius} cx="428" cy="428" />
      <circle
        className="progress"
        r={radius}
        cx="428"
        cy="428"
        strokeDasharray={circumference}
        strokeDashoffset={calculateStrokeDashoffset(
          circumference,
          timeInfo.remainingTimeInCurrentLevelMs,
          timeInfo.currentLevel?.time &&
            timeInfo.elapsedTimeMs &&
            timeInfo.elapsedTimeMs > 0
            ? timeInfo.currentLevel.time * 60000
            : null
        )}
      />
    </Circle>
  );
};

interface ProgressOverlayProps {
  timeInfo: TimerInfoState;
}

const ProgressOverlay: React.FC<ProgressOverlayProps> = ({ timeInfo }) => {
  return (
    <div className="dash-wrapper">
      <img
        src={
          timeInfo.currentLevel?.type === "LEVEL" &&
          timeInfo.elapsedTimeMs &&
          timeInfo.elapsedTimeMs > 0
            ? "/image-web/timer/dash-progress.png"
            : "/image-web/timer/break-dash-progress.png"
        }
        alt="Timer Progress"
      />
      <CircularProgress
        progress={
          timeInfo.currentLevel && timeInfo.remainingTimeInCurrentLevelMs
            ? calculatePercentage(
                timeInfo.remainingTimeInCurrentLevelMs,
                timeInfo.currentLevel?.time * 60000
              ) - 0.5
            : 0
        }
      />
    </div>
  );
};

interface CircularTimerContainerProps {
  timeInfo: TimerInfoState;
  data: TournamentResponse | null;
  currentLevel: StructuresResponse | null;
  nextLevel: StructuresResponse | null;
  isGameStarted: boolean;
}

const CircularTimerContainer: React.FC<CircularTimerContainerProps> = ({
  timeInfo,
  data,
  currentLevel,
  nextLevel,
  isGameStarted,
}) => {
  const RADIUS = 418;

  return (
    <CircularTimerWrapper>
      <CircularTimer radius={RADIUS} timeInfo={timeInfo} />
      <ProgressOverlay timeInfo={timeInfo} />
      <TimerDisplay
        type={timeInfo.currentLevel?.type || "LEVEL"}
        level={timeInfo.currentLevel?.level}
        remainingTime={timeInfo.remainingTimeInCurrentLevel}
        hasStarted={!!data?.timerDto}
        elapsedTimeMs={timeInfo?.elapsedTimeMs ?? 0}
        currentLevel={currentLevel}
        nextLevel={nextLevel}
        isGameStarted={isGameStarted}
      />
    </CircularTimerWrapper>
  );
};

export default CircularTimerContainer;
